import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import AppCard from './AppCard';

const AppSelector = () => {
  const navigate = useNavigate();
  const [selectedApp, setSelectedApp] = useState(null);

  const { data: userApps, isLoading, error } = useQuery({
    queryKey: ['userApps'],
    queryFn: async () => {
      const response = await api.get('/appmodule/user-apps');
      return response.data;
    },
  });

  const handleSelectApp = async (app) => {
    setSelectedApp(app.id);
    try {
      await api.post(`/appmodule/select-app/${app.id}`);
      
      // Navigate to the app's route
      if (app.route) {
        navigate(app.route);
      }
    } catch (err) {
      console.error('Error selecting app:', err);
      setSelectedApp(null);
    }
  };

  // If user only has one app, navigate directly to it
  useEffect(() => {
    if (userApps && userApps.length === 1 && userApps[0].route) {
      navigate(userApps[0].route);
    }
  }, [userApps, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your applications...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Error loading applications. Please try again.</p>
        </div>
      </div>
    );
  }

  if (!userApps || userApps.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No applications available</h3>
          <p className="mt-1 text-sm text-gray-500">
            Contact your administrator to request access to applications.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            G2M Portal Suite
          </h1>
          <p className="mt-2 text-lg text-gray-600 dark:text-gray-300">
            Select an application to continue
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {userApps.map((app) => (
            <AppCard
              key={app.id}
              app={app}
              onSelect={handleSelectApp}
              isSelecting={selectedApp === app.id}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AppSelector;